import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Plate } from "@/components/StatusBadge";
import { WORKSHOP_STAGES, label, formatDate, daysSince } from "@/lib/constants";
import { Wrench } from "lucide-react";

const ACTIVE = ["UNIT_IN", "REPAIR_ORDER", "READY_TO_DELIVERY"];

export default function Workshop() {
  const [claims, setClaims] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/claims").then((r) => setClaims(r.data.filter((c) => c.workshop_stage && ACTIVE.includes(c.status))));
  }, []);

  const byStage = (stage) => claims.filter((c) => c.workshop_stage === stage);

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-[#111827] sm:text-3xl">Workshop</h1>
      <p className="mt-1 text-sm text-gray-500">{claims.length} vehicle{claims.length !== 1 ? "s" : ""} currently in production</p>

      {/* Desktop kanban */}
      <div className="mt-6 hidden gap-4 overflow-x-auto pb-4 lg:flex">
        {WORKSHOP_STAGES.map((stage) => (
          <div key={stage} data-testid={`ws-col-${stage}`} className="w-64 shrink-0">
            <div className="mb-2 flex items-center justify-between px-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label(stage)}</span>
              <span className="rounded-full bg-gray-100 px-2 text-xs text-gray-600">{byStage(stage).length}</span>
            </div>
            <div className="space-y-2">
              {byStage(stage).map((c) => (
                <button key={c.id} data-testid={`ws-card-${c.id}`} onClick={() => navigate(`/claims/${c.id}`)} className="block w-full rounded-lg border border-gray-100 bg-white p-3 text-left shadow-sm transition-colors hover:border-[#3B82F6]">
                  <Plate value={c.license_plate} />
                  <div className="mt-2 text-sm font-medium text-gray-800">{[c.vehicle_brand, c.vehicle_model].filter(Boolean).join(" ")}</div>
                  <div className="text-xs text-gray-500">In: {formatDate(c.unit_in_date)} · {daysSince(c.unit_in_date)}d</div>
                </button>
              ))}
              {byStage(stage).length === 0 && <div className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-xs text-gray-300">Empty</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile vertical cards (no horizontal scroll) */}
      <div className="mt-6 space-y-3 lg:hidden">
        {claims.map((c) => (
          <button key={c.id} data-testid={`ws-mcard-${c.id}`} onClick={() => navigate(`/claims/${c.id}`)} className="block w-full rounded-lg border border-gray-100 bg-white p-4 text-left shadow-sm">
            <div className="flex items-center justify-between">
              <Plate value={c.license_plate} />
              <span className="inline-flex items-center gap-1 rounded-full bg-[#3B82F6]/15 px-2.5 py-0.5 text-xs font-semibold text-[#8a6d1a]"><Wrench size={12} /> {label(c.workshop_stage)}</span>
            </div>
            <div className="mt-3 text-sm font-medium text-gray-800">{[c.vehicle_brand, c.vehicle_model].filter(Boolean).join(" ") || "-"}</div>
            <div className="text-sm text-gray-500">{c.customer_name}</div>
            <div className="mt-2 text-xs text-gray-500">In: {formatDate(c.unit_in_date)} · {daysSince(c.unit_in_date)} day(s) in workshop</div>
          </button>
        ))}
        {claims.length === 0 && <p className="py-8 text-center text-sm text-gray-400">No vehicles in production.</p>}
      </div>
    </div>
  );
}
